import * as CL from '@design-system/component-library';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { emailMsg, invoiceLanguageMsg, recipientMsg, t } from '../../common/i18n/index.js';
import {
  FORM_GRID_FULL_WIDTH,
  FormGridFieldset,
} from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { Contact } from '../../common/react-hook-form/fields/Contact.js';
import { DeliveryMethod } from '../../common/react-hook-form/fields/DeliveryMethod.js';
import { ElectronicAddress } from '../../common/react-hook-form/fields/ElectronicAddress.js';
import { ElectronicOperator } from '../../common/react-hook-form/fields/ElectronicOperator.js';
import { Email } from '../../common/react-hook-form/fields/Email.js';
import { Language } from '../../common/react-hook-form/fields/Language.js';
import { ReceiverTypes, getDefaultElectronicOperator } from '../../common/utils/billingAccountUtils.js';
import { DEFAULT_BILLING_ACCOUNT_LANGUAGE } from '../../common/utils/billingAccountUtilsCoCo.js';
import { BillDeliveryMethod } from '../../generated/api/billDeliveryMethod.js';
import { BillingAccountDeliveryMethod } from '../../generated/api/billingAccountDeliveryMethod.js';
import { ContactType } from '../../generated/api/contactType.js';
import type { BillingAccountCocoFormValues } from './billingAccountCocoFormModel.js';
import type { EOperatorItem } from '../../common/utils/billingAccountUtils.js';
import type { BillChannel } from '../../generated/api/billChannel.js';
import type { Contact as ContactEntityType } from '../../generated/api/contact.js';

export const deliveryMethodCocoDefaultValues = (billChannels?: BillChannel[]) => {
  const defaultOperatorItem = getDefaultElectronicOperator(billChannels);
  const defaultElectronicAddress =
    defaultOperatorItem?.active && defaultOperatorItem?.electronicAddress ? defaultOperatorItem.electronicAddress : '';

  return {
    billElectronicAddress: defaultElectronicAddress,
    billElectronicOperator: defaultOperatorItem?.id || '',
    billLanguage: DEFAULT_BILLING_ACCOUNT_LANGUAGE,
    deliveryMethod: BillDeliveryMethod.IdEnum.ELECTRONIC,
    billReceiverType: ContactType.PERSON,
    billReceiverName: '',
    additionalBillReceiverEmails: [],
  };
};

interface DeliveryMethodFieldsetCocoProps {
  billChannels: BillChannel[];
  contacts: ContactEntityType[];
  onCreateBillReceiverContact: () => void;
}

export const DeliveryMethodFieldsetCoCo = ({
  billChannels,
  contacts,
  onCreateBillReceiverContact,
}: DeliveryMethodFieldsetCocoProps) => {
  const methods = useFormContext<BillingAccountCocoFormValues>();
  const values = methods.watch();

  const handleOperatorChange = (selectedOperator?: EOperatorItem) => {
    methods.setValue(
      'billingAccount.billElectronicAddress',
      selectedOperator?.active && selectedOperator.electronicAddress ? selectedOperator.electronicAddress : ''
    );
    methods.clearErrors('billingAccount.billElectronicAddress');
  };

  return (
    <FormGridFieldset>
      <DeliveryMethod name="billingAccount.deliveryMethod" billChannels={billChannels} />
      <Language name="billingAccount.billLanguage" label={t.A7DR(invoiceLanguageMsg)} />

      {values.billingAccount.deliveryMethod === BillingAccountDeliveryMethod.ELECTRONIC && (
        <>
          <ElectronicOperator
            name="billingAccount.billElectronicOperator"
            billChannels={billChannels}
            onOperatorChange={handleOperatorChange}
          />
          <ElectronicAddress name="billingAccount.billElectronicAddress" />
        </>
      )}
      {values.billingAccount.deliveryMethod === BillingAccountDeliveryMethod.EMAIL && (
        <>
          <div>
            <SelectRadio
              name="billReceiverSelection"
              label={t.SY1D(recipientMsg)}
              items={[
                {
                  label: t.ZCX7('Same as contact'),
                  value: ReceiverTypes.SAME_AS_CONTACT,
                },
                {
                  label: t.C6WY('Other'),
                  value: ReceiverTypes.OTHER,
                },
              ]}
            />
          </div>
          <div />
          {values.billReceiverSelection === ReceiverTypes.OTHER && (
            <>
              <Contact
                name="billingAccount.billReceiverId"
                contacts={contacts}
                reference="receiver"
                onCreateContact={onCreateBillReceiverContact}
              />
              <div />
              <div />
              <div />
            </>
          )}
          <div>
            <h4>{t.UZ3K('Other receiver')}</h4>
          </div>
          <div />
          {values.billingAccount.additionalBillReceiverEmails.map((_, index) => (
            <Email
              key={`additionalemail${index + 1}`}
              required={false}
              name={`billingAccount.additionalBillReceiverEmails[${index}]`}
              label={`${t.OKYY(emailMsg)} ${index + 1}`}
            />
          ))}
          {(values.billingAccount.additionalBillReceiverEmails || []).length < 5 && (
            <div className={FORM_GRID_FULL_WIDTH}>
              <CL.Button
                className={dsClass.MARGIN_BOTTOM_4}
                onClick={() => {
                  methods.setValue('billingAccount.additionalBillReceiverEmails', [
                    ...(values.billingAccount.additionalBillReceiverEmails || []),
                    '',
                  ]);
                }}
                color="light"
              >
                {t.N41Q('+ Add new')}
              </CL.Button>
            </div>
          )}
        </>
      )}
    </FormGridFieldset>
  );
};
